import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { PATHS } from './paths';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';
import { MainLayout } from '../components/layout';
import { FeatureGate } from '../components/common/FeatureGate/FeatureGate';

// Loading component for Suspense
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

// Lazy loaded pages - Public
const Landing = lazy(() => import('../pages/Landing').then(m => ({ default: m.Landing })));
const Register = lazy(() => import('../pages/Register').then(m => ({ default: m.Register })));
const Login = lazy(() => import('../pages/Login').then(m => ({ default: m.Login })));

// Lazy loaded pages - App
const Dashboard = lazy(() => import('../pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Produtos = lazy(() => import('../pages/Produtos').then(m => ({ default: m.Produtos })));
const Categorias = lazy(() => import('../pages/Categorias').then(m => ({ default: m.Categorias })));
const Clientes = lazy(() => import('../pages/Clientes').then(m => ({ default: m.Clientes })));
const Vendas = lazy(() => import('../pages/Vendas').then(m => ({ default: m.Vendas })));
const VendaDetalhes = lazy(() => import('../pages/Vendas/VendaDetalhes').then(m => ({ default: m.VendaDetalhes })));
const PDV = lazy(() => import('../pages/PDV').then(m => ({ default: m.PDV })));
const Eventos = lazy(() => import('../pages/Eventos').then(m => ({ default: m.Eventos })));
const Pedidos = lazy(() => import('../pages/Pedidos').then(m => ({ default: m.Pedidos })));
const Caixa = lazy(() => import('../pages/Caixa').then(m => ({ default: m.Caixa })));
const Estoque = lazy(() => import('../pages/Estoque').then(m => ({ default: m.Estoque })));
const Usuarios = lazy(() => import('../pages/Usuarios').then(m => ({ default: m.Usuarios })));
const Relatorios = lazy(() => import('../pages/Relatorios').then(m => ({ default: m.Relatorios })));
const Devolucoes = lazy(() => import('../pages/Devolucoes').then(m => ({ default: m.Devolucoes })));
const Cupons = lazy(() => import('../pages/Cupons').then(m => ({ default: m.Cupons })));
const Credito = lazy(() => import('../pages/Credito').then(m => ({ default: m.Credito })));
const Subscription = lazy(() => import('../pages/Subscription').then(m => ({ default: m.Subscription })));

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Rotas publicas - Landing e Registro */}
        <Route path={PATHS.LANDING} element={<Landing />} />
        <Route path={PATHS.REGISTER} element={<Register />} />

        {/* Rotas publicas - Login (redireciona se autenticado) */}
        <Route element={<PublicRoute />}>
          <Route path={PATHS.LOGIN} element={<Login />} />
        </Route>

        {/* Rotas privadas - App */}
        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path={PATHS.APP_HOME} element={<Navigate to={PATHS.DASHBOARD} replace />} />
            <Route path={PATHS.DASHBOARD} element={<Dashboard />} />
            <Route path={PATHS.PDV} element={<PDV />} />
            <Route path={PATHS.EVENTOS} element={<Eventos />} />
            <Route path={PATHS.PEDIDOS} element={<Pedidos />} />
            <Route path={PATHS.CAIXA} element={<Caixa />} />
            <Route path={PATHS.VENDAS} element={<Vendas />} />
            <Route path={PATHS.VENDA_DETALHES} element={<VendaDetalhes />} />
            <Route path={PATHS.PRODUTOS} element={<Produtos />} />
            <Route path={PATHS.CATEGORIAS} element={<Categorias />} />
            <Route path={PATHS.CLIENTES} element={<Clientes />} />
            <Route path={PATHS.SUBSCRIPTION} element={<Subscription />} />

            {/* Rotas restritas por perfil */}
            <Route element={<PrivateRoute roles={['ADMIN', 'GERENTE']} />}>
              <Route path={PATHS.ESTOQUE} element={<Estoque />} />
              <Route path={PATHS.RELATORIOS} element={<FeatureGate feature="RELATORIOS"><Relatorios /></FeatureGate>} />
              <Route path={PATHS.DEVOLUCOES} element={<FeatureGate feature="DEVOLUCOES"><Devolucoes /></FeatureGate>} />
              <Route path={PATHS.CUPONS} element={<FeatureGate feature="CUPONS"><Cupons /></FeatureGate>} />
              <Route path={PATHS.CREDITO} element={<FeatureGate feature="CREDITO"><Credito /></FeatureGate>} />
            </Route>

            <Route element={<PrivateRoute roles={['ADMIN']} />}>
              <Route path={PATHS.USUARIOS} element={<FeatureGate feature="MULTI_USUARIOS"><Usuarios /></FeatureGate>} />
            </Route>
          </Route>
        </Route>

        {/* Rota 404 */}
        <Route path="*" element={<Navigate to={PATHS.LANDING} replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
